import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { Form } from 'react-bootstrap';
import toastr from 'toastr';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe, useElements } from '@stripe/react-stripe-js';

import CreditCardInfoForm from './CreditCardInfoForm';
import BankAccountInfoForm from './BankAccountInfoForm';

import PickupWithBG from './PickupWithBG.svg';
import AlertWithIcon from '../alertWithIcon/AlertWithIcon';

import Requester from '../../helpers/requester';
import APIConstants from '../../helpers/api_constants';

/**
 * Handles callbacks from CC and Bank Info forms.
 * 1. Sends data to Stripe receives a PaymentMethod or Token
 * 2. On success, invokes `onSubmit` prop (from SignupFormWrapper) with
 *    payment method ID or token ID
 */

function PaymentSourceForm({ onSubmit, goBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isCreditCardPayment, setIsCreditCardPayment] = useState(true);

  // on mount, scroll to top of page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const togglePaymentType = () => {
    setIsCreditCardPayment(!isCreditCardPayment);
  };

  const handleStripeError = (error) => {
    toastr.error(
      'There was a problem with your payment info.',
      error?.message || 'Please try again or reach out to our support team for more help!',
      { closeButton: true, timeOut: 0 },
    );
  };

  const handleCreditCardSubmit = async () => {
      if (!stripe || !elements) return;

      // CardElement is rendered inside CreditCardInfoForm via Stripe Elements.
      // We retrieve it here to create the PaymentMethod.
      const cardElement = elements.getElement('card');

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });

      if (error) {
        handleStripeError(error);
        return;
      }

      onSubmit('credit_card', paymentMethod.id);
    };

  const handleACHSubmit = async (values) => {
    if (!stripe) return;

    // Use Stripe token API for bank account details.
    const { error, token } = await stripe.createToken('bank_account', {
      country: 'US',
      currency: 'usd',
      account_holder_name: values.name,
      account_holder_type: values.accountType, // 'individual' or 'company'
      routing_number: values.routingNumber,
      account_number: values.accountNumber,
    });

    if (error) {
      handleStripeError(error);
      return;
    }

    onSubmit('ach', token.id);
  };

  return (
    <div>
      <AlertWithIcon
        icon={PickupWithBG}
        alt="Shopping bag and a dollar sign icon."
      >
        <p>
          Don't worry - you won't be charged until you choose a monthly plan or
          carry out a pickup. Your payment information is stored very securely.
        </p>
        <p>
          Want more information before signing up?
        </p>
        <p>
          <a href="https://youtu.be/BhTjXMwDuWk" target="_blank">
            Click here to view Replate's donor account demo!
          </a>
        </p>
      </AlertWithIcon>
      <div className="title-name-row">
        <h3>
          Add Payment Details
        </h3>
      </div>
      <form>
        <Form.Group>
          <Form.Check
            id="creditCard"
            inline
            type="radio"
            label="Credit Card"
            onChange={togglePaymentType}
            checked={isCreditCardPayment === true}
          />
          <Form.Check
            id="ach"
            inline
            type="radio"
            label="ACH"
            onChange={togglePaymentType}
            checked={isCreditCardPayment === false}
          />
        </Form.Group>
      </form>

      { isCreditCardPayment ? (
        <CreditCardInfoForm
          handleCreditCardSubmit={handleCreditCardSubmit}
          goBack={goBack}
        />
      ) : (
        <BankAccountInfoForm
          handleACHSubmit={handleACHSubmit}
          goBack={goBack}
        />
      )}
    </div>
  );
}

PaymentSourceForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
};

/**
 * Wraps PaymentSourceForm with Stripe's Elements provider so that
 * useStripe() and useElements() are available inside the form.
 */
export default function PaymentSourceFormWrapper({ onSubmit, goBack }) {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    Requester.get(
      APIConstants.stripe.public_key,
      (data) => setStripePromise(loadStripe(data.key)),
    );
  }, []);

  return (
    <Elements stripe={stripePromise}>
      <PaymentSourceForm onSubmit={onSubmit} goBack={goBack} />
    </Elements>
  );
}

PaymentSourceFormWrapper.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
};
